import { MouseEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { materialCells } from '@jsonforms/material-renderers';
import { JsonForms } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { Control, ControllerRenderProps, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';
import { endpoints, getIDFromUrl, Url } from '@rossum/api-client';
import { SchemaSection } from '@rossum/api-client/schemas';
import { snakeToCamel } from '@rossum/api-client/utils';
import { ContentTitle } from '@rossum/components/content-group/ContentTitle';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { SplitContent } from '@rossum/components/content-group/SplitContent';
import { IconDotsVertical } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { useApiClient } from '../../../../../lib/apiClient';
import { isNotNullOrUndefined } from '../../../../../lib/typeGuards';
import { renderers } from '../../../../extension-interop/custom-controls/renderers';
import { intentSchema } from '../../../../extension-interop/hooks/intent.schema';
import { useHookInterfaces } from '../../../../extension-interop/hooks/useHookInterfaces';
import { FieldsFormValues } from '../../form-model/formModels';
import { IndexFormula } from './IndexFormula';
import { customMemoPayloadSchema } from './utils';

const builtinMemos = ['master_data_hub'] as const;
type BuiltinMemoId = (typeof builtinMemos)[number];

export const useInvokeHookInterfaceQuery = ({
  hookInterface,
}: {
  hookInterface: Url | undefined;
}) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['invoke_hook_interface', hookInterface],
    enabled: hookInterface !== undefined,
    queryFn: async () => {
      if (hookInterface === undefined) {
        throw new Error('Hook interface is required');
      }

      const response = await api.request(
        endpoints.hookInterfaces.invoke(getIDFromUrl(hookInterface), {
          variant: 'configure',
        })
      );
      return z
        .object({
          intent: intentSchema.optional(),
        })
        .parse(response);
    },
  });
};

type BuiltinMemosProps = {
  componentName: BuiltinMemoId;
  value: z.infer<typeof customMemoPayloadSchema>;
  onChange: (value: z.infer<typeof customMemoPayloadSchema>) => void;
  schemaContent: SchemaSection[];
  lookupSchemaId: string;
  description: ReactNode;
};

export const BuiltinMemos = ({
  componentName,
  value,
  onChange,
  schemaContent,
  lookupSchemaId,
  description,
}: BuiltinMemosProps) => {
  const intl = useIntl();

  if (componentName !== 'master_data_hub') return null;

  return (
    <SplitContent
      description={
        <Stack spacing={2}>
          <Typography variant="body2">{description}</Typography>
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'features.queueSettings.fields.memory.indexFormula.description',
            })}
          </Typography>
        </Stack>
      }
    >
      <IndexFormula
        schemaContent={schemaContent}
        value={value.index_formula ?? ''}
        onChange={newValue => {
          onChange({
            ...value,
            index_formula: newValue,
          });
        }}
        currentSchemaId={lookupSchemaId}
      />
      <TextField
        label={intl.formatMessage({
          id: 'features.queueSettings.fields.memory.builtin.masterDataHub.dataset.label',
        })}
        helperText={intl.formatMessage({
          id: 'features.queueSettings.fields.memory.builtin.masterDataHub.dataset.description',
        })}
        value={(value.configuration?.['dataset'] as string | undefined) ?? ''}
        onChange={e => {
          onChange({
            ...value,
            configuration: { ...value.configuration, dataset: e.target.value },
          });
        }}
        fullWidth
        size="small"
      />
    </SplitContent>
  );
};

export const CustomMemoContent = ({
  value,
  onChange,
  schemaContent,
  lookupSchemaId,
  description,
}: {
  value: z.infer<typeof customMemoPayloadSchema>;
  onChange: (value: z.infer<typeof customMemoPayloadSchema>) => void;
  schemaContent: SchemaSection[];
  lookupSchemaId: string;
  description: ReactNode;
}) => {
  const intl = useIntl();

  const componentName = builtinMemos.find(id => id === value.type);

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    error,
  } = useInvokeHookInterfaceQuery({
    hookInterface:
      componentName === undefined ? value.hook_interface : undefined,
  });

  return componentName ? (
    <BuiltinMemos
      componentName={componentName}
      value={value}
      onChange={onChange}
      schemaContent={schemaContent}
      lookupSchemaId={lookupSchemaId}
      description={description}
    />
  ) : isFetching ? (
    <LinearProgress />
  ) : isLoading ? null : isError ? ( // isLoading here means idle = no hook interface
    <Alert color="error">{error?.toString()}</Alert>
  ) : !response.intent ? (
    <Alert color="error">
      {intl.formatMessage({
        id: 'features.queueSettings.rules.actions.documentAction.custom.errorIntent',
      })}
    </Alert>
  ) : (
    <SplitContent
      description={
        <Stack spacing={2}>
          <Typography variant="body2">{description}</Typography>
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'features.queueSettings.fields.memory.indexFormula.description',
            })}
          </Typography>
        </Stack>
      }
    >
      <IndexFormula
        schemaContent={schemaContent}
        value={value.index_formula ?? ''}
        onChange={newValue => {
          onChange({
            ...value,
            index_formula: newValue,
          });
        }}
        currentSchemaId={lookupSchemaId}
      />

      {response.intent.error ? (
        <Alert color="error">{response.intent.error.message}</Alert>
      ) : null}

      {response.intent.info ? (
        <Alert color="info">{response.intent.info.message}</Alert>
      ) : null}

      {response.intent.form ? (
        <Stack>
          <JsonForms
            schema={response.intent?.form?.schema as JsonSchema}
            uischema={response.intent?.form?.uiSchema as UISchemaElement}
            data={value.configuration}
            validationMode="NoValidation"
            renderers={renderers}
            cells={materialCells}
            onChange={({ data }) => {
              if (data !== value.configuration) {
                onChange({
                  hook_interface: value.hook_interface,
                  index_formula: value.index_formula,
                  configuration: data,
                });
              }
            }}
          />
        </Stack>
      ) : null}
    </SplitContent>
  );
};

export const CustomMemo = ({
  field,
  schemaContent,
  control,
}: {
  field: ControllerRenderProps<FieldsFormValues, 'field.memory'>;
  schemaContent: SchemaSection[];
  control: Control<FieldsFormValues>;
}) => {
  const intl = useIntl();
  const { data: hookInterfaces } = useHookInterfaces({
    canInvoke: true,
    type: 'queue_memory',
  });

  const datasetResult = customMemoPayloadSchema.safeParse(field.value);

  const isMemoryConfigured =
    datasetResult.success &&
    (datasetResult.data.type !== undefined ||
      datasetResult.data.hook_interface !== undefined);

  const memoOptions = useMemo(
    () => [
      ...builtinMemos.filter(isNotNullOrUndefined).map(id => ({
        id,
        type: 'out_of_the_box' as const,
        name: intl.formatMessage({
          id: `features.queueSettings.fields.memory.builtin.${snakeToCamel(id)}.title` as const,
        }),
        description: intl.formatMessage({
          id: `features.queueSettings.fields.memory.builtin.${snakeToCamel(id)}.description` as const,
        }),
      })),
      ...(hookInterfaces?.results ?? []).map(i => ({
        ...i,
        type: 'hook_interface' as const,
      })),
    ],
    [hookInterfaces?.results, intl]
  );

  const value =
    memoOptions.find(option =>
      datasetResult.success
        ? (option.type === 'hook_interface' &&
            datasetResult.data.hook_interface === option.url) ||
          (option.type === 'out_of_the_box' &&
            datasetResult.data.type === option.id)
        : false
    ) ?? null;

  const lookupSchemaId = useWatch({ control, name: 'field.id' });

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleMenuSelect = useCallback(
    (option: (typeof memoOptions)[number] | null) => {
      field.onChange(
        option
          ? {
              hook_interface:
                option.type === 'hook_interface' ? option.url : undefined,
              type: option.type === 'out_of_the_box' ? option.id : undefined,
              ...(option.id === 'master_data_hub'
                ? { configuration: { dataset: `memory_${lookupSchemaId}` } }
                : {}),
            }
          : {}
      );
      handleMenuClose();
    },
    [field, handleMenuClose, lookupSchemaId]
  );

  return (
    <ContentWrapper>
      <Stack>
        <ContentTitle
          title={intl.formatMessage({
            id: 'features.queueSettings.fields.memory.title',
          })}
          description={
            !isMemoryConfigured
              ? intl.formatMessage({
                  id: 'features.queueSettings.fields.memory.emptyState',
                })
              : undefined
          }
          titleAction={
            <>
              <IconButton size="small" onClick={handleMenuOpen}>
                <SvgIcon fontSize="large">
                  <IconDotsVertical />
                </SvgIcon>
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { maxWidth: 300 } } }}
              >
                {isMemoryConfigured
                  ? [
                      <MenuItem
                        key="none"
                        onClick={() => handleMenuSelect(null)}
                      >
                        <ListItemText
                          primary={intl.formatMessage({
                            id: 'features.queueSettings.fields.memory.disable',
                          })}
                        />
                      </MenuItem>,
                      <Divider key="divider" />,
                    ]
                  : null}
                {memoOptions.map(option => (
                  <MenuItem
                    key={option.id}
                    selected={value?.id === option.id}
                    onClick={() => handleMenuSelect(option)}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ width: 'fit-content' }}
                        >
                          <span>{option.name}</span>
                          {option.type === 'hook_interface' ? (
                            <Chip
                              label={intl.formatMessage({
                                id: 'features.queueSettings.fields.hookInterface.chip',
                              })}
                              size="small"
                            />
                          ) : null}
                        </Stack>
                      }
                      secondary={option.description}
                      secondaryTypographyProps={{
                        sx: { whiteSpace: 'normal' },
                      }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          }
        />
        {isMemoryConfigured ? (
          <CustomMemoContent
            description={
              value ? (
                <Typography variant="body2">{value.description}</Typography>
              ) : (
                intl.formatMessage({
                  id: 'features.queueSettings.fields.memory.description',
                })
              )
            }
            value={datasetResult.data}
            onChange={field.onChange}
            schemaContent={schemaContent}
            lookupSchemaId={lookupSchemaId}
          />
        ) : null}
      </Stack>
    </ContentWrapper>
  );
};
